/**
 * Pure helpers + design tokens for the bookings calendar. Pulled out so the
 * main `BookingsCalendar` component can stay focused on state + composition.
 */

export const MS_DAY = 86400000;
export const POLL_MS = 30000;
// Width of one day column. Used by drag math, grid sizing, and absolute-
// positioned overlays. Keep in sync if you change it.
export const COL_W = 44;

// Iter 65: status colour map shared between booking cards (3-sided frames) and the
// Live Bookings Calendar (gantt blocks). Keep these in lockstep so vendors learn the
// language once and recognise it everywhere.
export const STATUS_STYLE = {
  // Iter 100 — spec §1 color scheme, consistent across admin + vendor.
  confirmed:       { bg: "bg-blue-500",    text: "text-white",     label: "Reserved",        border: "border-blue-600" },
  active:          { bg: "bg-orange-500",  text: "text-white",     label: "Active Rental",   border: "border-orange-600" },
  pending_payment: { bg: "bg-yellow-400",  text: "text-yellow-950", label: "Payment Pending", border: "border-yellow-500" },
  pending:         { bg: "bg-yellow-400",  text: "text-yellow-950", label: "Hold",            border: "border-yellow-500" },
  cancelled:       { bg: "bg-neutral-200", text: "text-neutral-500", label: "Cancelled",      border: "border-neutral-300" },
  completed:       { bg: "bg-neutral-400", text: "text-white",     label: "Completed",       border: "border-neutral-500" },
};

/** Iter 100 — derived display status for a calendar booking bar. */
export const bookingBarStatus = (b) => {
  const s = (b.status || "").toLowerCase();
  if (s !== "confirmed") return STATUS_STYLE[s] ? s : "confirmed";
  const today = toLocalIso(new Date());
  const start = String(b.start || "").slice(0, 10);
  const end = String(b.end || "").slice(0, 10);
  if (start && end && start <= today && today <= end && !b.returned_at) return "active";
  return "confirmed";
};

export const formatHeaderDay = (d) => d.toLocaleDateString("en-GB", { weekday: "short", day: "2-digit" });

export const formatRangeDay = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

export const formatTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const h = d.getUTCHours();
  const m = d.getUTCMinutes();
  if (h === 0 && m === 0) return "";
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

// Convert ISO → days-since-window-start. Fractional so half-day pickups land between cells.
export const dayOffset = (iso, windowStart) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return (d.getTime() - windowStart.getTime()) / MS_DAY;
};

import { toLocalIso } from "@/lib/localDate";

// Add/subtract whole days to an ISO date or datetime string. Preserves the time
// portion so a vendor who originally created `…T14:00:00Z` keeps that pickup
// hour after a resize.
export const addDaysToIso = (iso, deltaDays) => {
  if (!iso) return iso;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  // Iter 65 P2: when input is a pure YYYY-MM-DD date, do the arithmetic in LOCAL
  // time so it matches what the user sees in date pickers. Datetime values keep
  // their original timezone behaviour.
  if (!iso.includes("T")) {
    d.setDate(d.getDate() + deltaDays);
    return toLocalIso(d);
  }
  d.setUTCDate(d.getUTCDate() + deltaDays);
  return d.toISOString();
};

// Day index → ISO date string aligned to a given window-start anchor. Always
// returns a LOCAL YYYY-MM-DD (matches the calendar header rendering).
export const dayIndexToIso = (idx, windowStart) => {
  const d = new Date(windowStart);
  d.setDate(d.getDate() + idx);
  return toLocalIso(d);
};
